import { DocumentNode } from '@apollo/client';
import { Box, Spinner, useToast, useDisclosure } from '@chakra-ui/react';
import { dinero, isZero } from 'dinero.js';
import * as React from 'react';

import { AddItemToCartInput, CartItemType, QuantityMode } from '@/graphql-types/globalTypes';
import { CartItemTypeEnum } from '@/imports/ui/components/Cart/TypeDefs';
import {
  TicketSelect_EventFragment,
} from '../graphql/graphql-types/TicketSelect_EventFragment';
import { useCurrent } from '@/hooks/useCurrent';
import { useCart } from '@/state/cart/useCart';

import { MultiTicketWarningModal } from './MultiTicketWarningModal';
import { PurchaserParticipationModal } from './PurchaserParticipationModal';
import { TICKET_MESSAGES } from './constants';
import { TicketItem } from './TicketItem';
import { getAudienceLabel, getAudienceBadgeColor } from './ticketUtils';
import { TICKET_SELECT_EVENT_FRAGMENT } from '../graphql/queries';
import { useUserGroups } from '../hooks/useUserGroups';
import { useEventRegistrations } from '../hooks/useEventRegistrations';
import { useTicketEligibility } from '../hooks/useTicketEligibility';
import { useAttendeeValidation } from '@/imports/ui/components/Cart/hooks/useAttendeeValidation';
import { shouldUseMemberLearningRegistrationCopy } from '@/utilities/memberLearningRegistrationCopy';

export const TicketSelect: React.FC<{
  isDisabled?: boolean;
  event: any;
  eventId?: string;
  purchaserData?: any;
  isFromInvoice?: boolean;
  hasMembershipInInvoice?: boolean;
}> & { eventFragment: DocumentNode } = (props) => {
  const { user } = useCurrent();
  const toast = useToast();
  const { items: cartItems, addToCart, updateQuantity, removeFromCart } = useCart();
  const { isOpen: isWarningOpen, onOpen: onWarningOpen, onClose: onWarningClose } = useDisclosure();
  const { isOpen: isParticipationOpen, onOpen: onParticipationOpen, onClose: onParticipationClose } = useDisclosure();

  const [pendingIncrement, setPendingIncrement] = React.useState<{
    ticketId: string;
    ticketName: string;
    audienceLabel: string;
  } | null>(null);

  // Track purchase intent for each ticket - key is ticketId, value is 'self-only' or 'for-others'
  const [purchaseIntent, setPurchaseIntent] = React.useState<Record<string, 'self-only' | 'for-others'>>({});

  const hasMembershipInCart = cartItems.some(
    (item) => item.type === CartItemTypeEnum.MEMBERSHIP
  );

  // Custom hooks
  const { userGroupIds } = useUserGroups(user, props?.purchaserData);
  const {
    registrationsData,
    registrationsLoading,
    getIsUserRegisteredForTicket,
  } = useEventRegistrations(
    props.eventId,
    props?.purchaserData?._id ? props?.purchaserData : user
  );
  const eligibility = useTicketEligibility(props.event, props?.purchaserData?._id ? props?.purchaserData : user, hasMembershipInCart, userGroupIds, registrationsData, props?.isFromInvoice);
  const attendeeValidation = useAttendeeValidation();

  const useEnrollmentCopy = shouldUseMemberLearningRegistrationCopy(
    props.event ?? {}
  );

  // Helper functions using existing attendee validation
  const hasCheckoutAttendeesDetails = React.useCallback((ticketId: string): boolean => {
    if (!props.eventId) return false;

    // Get requirements for this specific event
    const eventRequirements = attendeeValidation.getRequirementsForEvent(props.eventId);
    const ticketRequirement = eventRequirements.find(req => req.ticketId === ticketId);

    // Has checkout details if there are any attendee IDs (saved forms)
    return ticketRequirement ? ticketRequirement.attendeeIds.length > 0 : false;
  }, [attendeeValidation, props.eventId]);

  const shouldSkipParticipationModal = React.useCallback((ticketId: string, isUserRegistered: boolean): boolean => {
    // Skip if user is either registered or has saved checkout details
    return isUserRegistered || hasCheckoutAttendeesDetails(ticketId);
  }, [hasCheckoutAttendeesDetails]);

  // Get current cart quantities for this event
  const getTicketQuantityInCart = React.useCallback((ticketId: string) => {
    return cartItems
      .filter(item =>
        (item.type === CartItemTypeEnum.EVENTS ||
          item.type === CartItemTypeEnum.COURSE) &&
        item.eventId === props.eventId &&
        item.eventTicketId === ticketId
      )
      .reduce((total, item) => total + item.quantity, 0);
  }, [cartItems, props.eventId]);

  // Direct cart operations
  const handleIncrement = React.useCallback(async (ticketId: string, ticketName?: string, audienceLabel?: string, ticket?: any) => {
    if (!user?._id) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in before adding tickets to your cart.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const currentQuantity = getTicketQuantityInCart(ticketId);
    const isMagicLinkTicket = ticket?.canPurchase?.type === 'MAGIC_LINK';
    const isUserRegistered = getIsUserRegisteredForTicket(ticketId);

    // Magic link tickets can only have quantity 1
    if (isMagicLinkTicket && currentQuantity >= 1) {
      toast({
        title: TICKET_MESSAGES.TOAST.CANNOT_ADD_MULTIPLE.TITLE,
        description: TICKET_MESSAGES.TOAST.CANNOT_ADD_MULTIPLE.DESCRIPTION,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if user previously selected "self-only" but is now trying to add more tickets
    if (currentQuantity >= 1 && purchaseIntent[ticketId] === 'self-only' && !props.isFromInvoice) {
      setPendingIncrement({ ticketId, ticketName: ticketName || '', audienceLabel: audienceLabel || '' });
      onWarningOpen();
      return;
    }

    // Show warning modal when incrementing from 1 to 2 (unless it's from invoice)
    if (currentQuantity === 1 && ticketName && audienceLabel && !props.isFromInvoice) {
      setPendingIncrement({ ticketId, ticketName, audienceLabel });
      onWarningOpen();
      return;
    }

    // Handle first ticket selection (0 to 1)
    if (currentQuantity === 0) {

      // If user should skip participation modal (either registered or has checkout details),
      // add directly to cart (defaulting to "purchasing for others")
      if (shouldSkipParticipationModal(ticketId, isUserRegistered)) {
        await addTicketToCart(ticketId, 1);
        setPurchaseIntent(prev => ({ ...prev, [ticketId]: 'for-others' }));
        return;
      }

      // Show participation modal for first-time users (unless it's from invoice)
      if (ticketName && audienceLabel && !props.isFromInvoice) {
        setPendingIncrement({ ticketId, ticketName, audienceLabel });
        onParticipationOpen();
        return;
      }
    }

    // Default increment
    await addTicketToCart(ticketId, currentQuantity + 1);
  }, [user, getTicketQuantityInCart, getIsUserRegisteredForTicket, shouldSkipParticipationModal, props.isFromInvoice, toast, onWarningOpen, onParticipationOpen, purchaseIntent]);

  const handleDecrement = React.useCallback(async (ticketId: string) => {
    const currentQuantity = getTicketQuantityInCart(ticketId);

    if (currentQuantity <= 1) {
      // Remove from cart completely and reset purchase intent
      const cartItem = cartItems.find(item =>
        item.eventTicketId === ticketId &&
        item.eventId === props.eventId
      );
      if (cartItem) {
        await removeFromCart(cartItem._id);
        // Reset purchase intent when ticket is removed from cart
        setPurchaseIntent(prev => {
          const newIntent = { ...prev };
          delete newIntent[ticketId];
          return newIntent;
        });
      }
    } else {
      // Decrease quantity
      await addTicketToCart(ticketId, currentQuantity - 1);
    }
  }, [getTicketQuantityInCart, cartItems, props.eventId, removeFromCart]);

  // Helper function to add/update ticket in cart
  const addTicketToCart = React.useCallback(async (
    ticketId: string,
    quantity: number,
    options?: { registerMe?: boolean }
  ) => {
    try {
      const existingCartItem = cartItems.find(item =>
        item.eventTicketId === ticketId &&
        item.eventId === props.eventId
      );

      if (existingCartItem) {
        // Update existing item
        await updateQuantity({ cartItemId: existingCartItem._id, quantity });
      } else {
        // Add new item
        const cartItem: AddItemToCartInput = {
          itemId: ticketId,
          itemType: CartItemType.EVENTS,
          eventId: props.eventId!,
          quantity,
          eventTicketId: ticketId,
          quantityMode: QuantityMode.SET,
          ...(options?.registerMe === true && { registerMe: true }),
        };
        await addToCart([cartItem]);
      }
    } catch (error) {
      console.error('Failed to update cart:', error);
      toast({
        title: 'Error',
        description: 'Failed to update cart. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [cartItems, props.eventId, updateQuantity, addToCart, toast]);

  // Modal handlers
  const confirmIncrement = React.useCallback(async () => {
    if (!pendingIncrement) return;

    const currentQuantity = getTicketQuantityInCart(pendingIncrement.ticketId);
    await addTicketToCart(pendingIncrement.ticketId, currentQuantity + 1);

    setPendingIncrement(null);
    onWarningClose();
  }, [pendingIncrement, getTicketQuantityInCart, addTicketToCart, onWarningClose]);

  const handleParticipationYes = React.useCallback(async () => {
    if (!pendingIncrement) return;

    await addTicketToCart(pendingIncrement.ticketId, 1, { registerMe: true });
    // User selected "Yes, register me" — they are one of the attendees
    setPurchaseIntent(prev => ({ ...prev, [pendingIncrement.ticketId]: 'self-only' }));

    setPendingIncrement(null);
    onParticipationClose();
  }, [pendingIncrement, addTicketToCart, onParticipationClose]);

  const handleParticipationNo = React.useCallback(async () => {
    if (!pendingIncrement) return;

    await addTicketToCart(pendingIncrement.ticketId, 1);
    // User selected "No, I'm just purchasing" — not registering themselves
    setPurchaseIntent(prev => ({ ...prev, [pendingIncrement.ticketId]: 'for-others' }));

    setPendingIncrement(null);
    onParticipationClose();
  }, [pendingIncrement, addTicketToCart, onParticipationClose]);

  const resetPendingIncrement = React.useCallback(() => {
    setPendingIncrement(null);
  }, []);

  if (registrationsLoading) {
    return <Spinner />;
  }

  const baseEvent = props.event as unknown as TicketSelect_EventFragment;
  if (!baseEvent.ticketsCurrentUserCanPurchase?.length) {
    return <Box textAlign="center">No tickets available</Box>;
  }

  return (
    <Box>
      {baseEvent.ticketsCurrentUserCanPurchase.map((ticket) => {
        const price = dinero(ticket.price);
        const isFree = isZero(price);
        const audienceLabel = getAudienceLabel(ticket);
        const badgeColor = getAudienceBadgeColor(audienceLabel);
        const eligibilityCheck = eligibility.getEligibilityForTicket(ticket);
        const quantity = getTicketQuantityInCart(ticket._id);
        const isRegistered = getIsUserRegisteredForTicket(ticket._id);
        const hasCheckoutDetails = hasCheckoutAttendeesDetails(ticket._id);

        return (
          <TicketItem
            key={ticket._id}
            ticket={{
              ...ticket,
              quantity,
              eventId: props.eventId,
              eventTicketId: ticket._id,
              itemId: ticket._id,
              itemType: CartItemType.EVENTS,
            }}
            eligibility={eligibilityCheck}
            audienceLabel={audienceLabel}
            badgeColor={badgeColor}
            isFree={isFree}
            isRegistered={isRegistered}
            hasCheckoutDetails={hasCheckoutDetails}
            onDecrement={handleDecrement}
            onIncrement={(id, name, label) => handleIncrement(id, name, label, ticket)}
          />
        );
      })}

      {pendingIncrement && (
        <MultiTicketWarningModal
          isOpen={isWarningOpen}
          onClose={() => {
            onWarningClose();
            resetPendingIncrement();
          }}
          onConfirm={confirmIncrement}
          audienceType={pendingIncrement.audienceLabel}
          ticketName={pendingIncrement.ticketName}
          quantity={2}
        />
      )}

      {pendingIncrement && (
        <PurchaserParticipationModal
          isOpen={isParticipationOpen}
          onClose={() => {
            onParticipationClose();
            resetPendingIncrement();
          }}
          onYes={handleParticipationYes}
          onNo={handleParticipationNo}
          useEnrollmentCopy={useEnrollmentCopy}
        />
      )}
    </Box>
  );
};

TicketSelect.eventFragment = TICKET_SELECT_EVENT_FRAGMENT;
